import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  User,
  Lock,
  Mail,
  Eye,
  EyeOff,
  Save,
  Shield,
  Bell,
  Palette,
  Star
} from 'lucide-react';

export const Settings = () => {
  const { user, api, isCOO } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    title: user?.title || ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Review links (COO only)
  const [reviewLinks, setReviewLinks] = useState({
    google_review_url: '',
    yelp_review_url: ''
  });
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    if (!isCOO) return;
    const fetchSettings = async () => {
      try {
        const res = await api.get('/portal/company-settings');
        setReviewLinks({
          google_review_url: res.data?.google_review_url || '',
          yelp_review_url: res.data?.yelp_review_url || ''
        });
      } catch (error) {
        console.error('Error fetching company settings:', error);
      }
    };
    fetchSettings();
  }, [api, isCOO]);

  const handleReviewLinksSave = async (e) => {
    e.preventDefault();
    setReviewLoading(true);
    try {
      await api.put('/portal/company-settings', reviewLinks);
      toast.success('Review links saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save review links');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    
    try {
      await api.patch('/auth/profile', profileData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'security', name: 'Security', icon: Shield },
    ...(isCOO ? [{ id: 'reviews', name: 'Review Links', icon: Star }] : []),
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'appearance', name: 'Appearance', icon: Palette },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-100 p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#C6A87C]/10 text-[#C6A87C] border-l-4 border-[#C6A87C]'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`settings-tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-100"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <User className="w-5 h-5 text-[#C6A87C]" />
                  Profile Information
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Update your account details and contact information
                </p>
              </div>

              <form onSubmit={handleProfileUpdate} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      data-testid="settings-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                        data-testid="settings-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title
                    </label>
                    <input
                      type="text"
                      value={profileData.title}
                      onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      data-testid="settings-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <input
                      type="text"
                      value={user?.role?.toUpperCase() || ''}
                      disabled
                      className="w-full px-4 py-3 border border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-400 mt-1">Role cannot be changed</p>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={profileLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="settings-save-profile-btn"
                  >
                    {profileLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-100"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#C6A87C]" />
                  Change Password
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Update your password to keep your account secure
                </p>
              </div>

              <form onSubmit={handlePasswordChange} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="w-full pl-12 pr-12 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      placeholder="Enter current password"
                      required
                      data-testid="settings-current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="w-full pl-12 pr-12 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      placeholder="Enter new password"
                      required
                      data-testid="settings-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="w-full pl-12 pr-12 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                      placeholder="Confirm new password"
                      required
                      data-testid="settings-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Password Requirements:</strong>
                  </p>
                  <ul className="text-sm text-yellow-700 mt-2 list-disc list-inside space-y-1">
                    <li>Minimum 6 characters</li>
                    <li>Mix of letters and numbers recommended</li>
                    <li>Avoid using common words or personal info</li>
                  </ul>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="settings-change-password-btn"
                  >
                    {passwordLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Lock className="w-5 h-5" />
                    )}
                    Change Password
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Review Links Tab (COO only) */}
          {activeTab === 'reviews' && isCOO && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-100"
              data-testid="settings-reviews-tab"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#C6A87C]" />
                  External Review Links
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  These links are shown to customers after they submit a review, encouraging them to also post on Google &amp; Yelp.
                </p>
              </div>

              <form onSubmit={handleReviewLinksSave} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Review URL
                  </label>
                  <input
                    type="url"
                    value={reviewLinks.google_review_url}
                    onChange={(e) => setReviewLinks({ ...reviewLinks, google_review_url: e.target.value })}
                    placeholder="https://g.page/r/.../review"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    data-testid="settings-google-review-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yelp Review URL
                  </label>
                  <input
                    type="url"
                    value={reviewLinks.yelp_review_url}
                    onChange={(e) => setReviewLinks({ ...reviewLinks, yelp_review_url: e.target.value })}
                    placeholder="https://www.yelp.com/biz/..."
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    data-testid="settings-yelp-review-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={reviewLoading}
                  className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors disabled:opacity-50"
                  data-testid="settings-save-reviews-btn"
                >
                  <Save className="w-5 h-5" />
                  {reviewLoading ? 'Saving...' : 'Save Review Links'}
                </button>
              </form>
            </motion.div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-100"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#C6A87C]" />
                  Notification Preferences
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Manage how you receive notifications
                </p>
              </div>

              <div className="p-6">
                <p className="text-gray-500 text-center py-8">
                  Notification settings coming soon...
                </p>
              </div>
            </motion.div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-100"
            >
              <div className="p-6 border-b border-gray-100">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#C6A87C]" />
                  Appearance Settings
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Customize how the portal looks
                </p>
              </div>

              <div className="p-6">
                <p className="text-gray-500 text-center py-8">
                  Appearance settings coming soon...
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};
